import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import TextInputPattern from '@mlb/react-patterns/components/TextInput/textInput.tsx';
import { styledButtonIcons } from '@mlb/react-patterns/components/TextInput/textInput.style';
import ButtonPattern from '@mlb/react-patterns/components/Button/button.tsx';

import useSearchQueryParam from 'bdvp-advanced-search/src/hooks/store/useSearchQueryParam';
import { QUERY_TYPE } from 'bdvp-advanced-search/src/utils/constants';
import { useMedia } from 'bdvp-common/src/hooks';
import { useDictionary } from 'bdvp-common/src/hooks/config';
import { QUERY_PARAMS, SEARCH_ROUTE } from 'bdvp-common/src/utils/constants';
import { Tracking } from 'bdvp-tracking';

const Wrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;
`;

const TextInput = styled(TextInputPattern)`
    width: 100%;

    input[type=search] {
        color: ${COLORS.mlbBlack};
        background: ${COLORS.mlbWhite};
        font-size: 16px;
        letter-spacing: 0.26px;
        padding-left: 10px;
        padding-right: 40px;
        height: 37px;
        border: solid 3px ${COLORS.transparent};

        ::placeholder {
            color: ${COLORS.mlbGrey7};
        }

        &:focus {
            border: solid 3px #e6e6e6;
        }
    }

    ${styledButtonIcons} {
        top: 0;
        margin-bottom: 10px;
        margin-top: 10px;
        background: ${COLORS.mlbWhite};
    }
`;

const Button = styled(ButtonPattern)`
    position: absolute;
    height: 37px;
    right: 0px;
    top: 0px;
`;

const CancelButton = styled(ButtonPattern)`
    color: ${COLORS.mlbWhite};
    margin-left: 5px;
`;

const SearchInput = (props) => {

    const { setShowFreeTextInput } = props;

    const {
        searchQuery,
        queryType,
    } = useSearchQueryParam();

    const [
        value,
        setValue,
    ] = useState(
        queryType === QUERY_TYPE.freetext
            ? (searchQuery ?? '').trim()
            : ''
    );

    const searchInputRef = useRef(null);

    const isMobile = useMedia([`(min-width: ${BREAKPOINTS.md})`], [false], true);
    const dictionary = useDictionary();
    const history = useHistory();

    useEffect(() => {

        let unsubscribeFromHistory = () => {};

        unsubscribeFromHistory = history.listen((newLocation) => {

            if (newLocation.pathname !== SEARCH_ROUTE) {

                setValue('');
                if (isMobile) {

                    setShowFreeTextInput(false);

                }

            }

        });

        return () => {

            unsubscribeFromHistory();

        };

    }, [
        history,
        isMobile,
        setShowFreeTextInput,
    ]);

    const executeAndTrackQuery = useCallback((searchText = '', wasClicked = true) => {

        const trimmedSearchText = searchText.trim();
        if(!trimmedSearchText) {

            return;

        }

        Tracking.trackFreeTextSearchClickEnterQuery(trimmedSearchText, wasClicked);

        const urlParams = new URLSearchParams();
        urlParams.set(QUERY_PARAMS.query, trimmedSearchText);
        urlParams.set(QUERY_PARAMS.queryType, QUERY_TYPE.freetext);
        history.push({
            pathname: SEARCH_ROUTE,
            search: urlParams.toString(),
        });

        searchInputRef.current.blur();

    }, [
        history,
        searchInputRef,
    ]);

    return (
        <Wrapper>
            <TextInput
                ariaLabel={dictionary.free_text_search ?? 'Free text search'}
                value={value}
                placeHolder={dictionary.search_text ?? 'Search'}
                handlers={
                    {
                        onChange: (event) => {

                            setValue(event.target.value);

                        },
                        onKeyPress: (event) => {

                            if (event.key === 'Enter') {

                                executeAndTrackQuery(value, false);

                            }

                        },
                        onFocus: () => {

                            if (!isMobile) {

                                Tracking.trackFreeTextSearchFocus();

                            }

                        },
                    }
                }
                cancelButton={isMobile}
                handleCancelButtonClick={() => {

                    searchInputRef.current.focus();
                    setValue('');
                    Tracking.trackFreeTextSearchReset();

                }}
                inputType={'search'}
                additionalProps={
                    {
                        enterkeyhint: 'search',
                        autoFocus: isMobile,
                    }
                }
                spellCheck={false}
                refElement={searchInputRef}
            />
            {isMobile
                ? (
                    <CancelButton
                        label={dictionary.cancel ?? 'Cancel'}
                        size={'md'}
                        shape={'hollow-borderless'}
                        styleName={'link-white'}
                        handlers={{
                            onClick: () => {

                                Tracking.trackFreeTextSearchCancel();
                                setShowFreeTextInput(false);

                            },
                        }}
                    />
                )
                : (
                    <Button
                        disabled={!!!value}
                        iconName={'search'}
                        iconColor={COLORS.mlbGrey7}
                        hasHover={false}
                        size={'lg'}
                        shape={'hollow-borderless'}
                        styleName={'default'}
                        handlers={
                            {
                                onClick: () => {

                                    executeAndTrackQuery(value);

                                },
                            }
                        }
                    />
                )
            }
        </Wrapper>
    );

};

SearchInput.propTypes = {
    setShowFreeTextInput: PropTypes.func,
};

SearchInput.defaultProps = {
    setShowFreeTextInput: () => {},
};

export default SearchInput;
